import React, { createContext, useContext } from 'react';
import { useDeviceDetection, DEVICE_TYPES } from '../hooks/useDeviceContext';

/**
 * Device Context for Transcend Platform
 * Provides device information throughout the app
 */

const DeviceContext = createContext(null);

/**
 * Device Provider Component
 * Wraps the app to provide device context to all children
 */
export const DeviceProvider = ({ children }) => {
  const deviceInfo = useDeviceDetection();

  return (
    <DeviceContext.Provider value={deviceInfo}>
      {children}
    </DeviceContext.Provider>
  );
};

/**
 * Hook to access device context
 * @returns {Object} Device information including:
 *   - deviceType: 'mobile' | 'tablet' | 'desktop'
 *   - isMobile: boolean
 *   - isTablet: boolean
 *   - isDesktop: boolean
 *   - isTouch: boolean
 *   - hasHover: boolean
 *   - orientation: 'portrait' | 'landscape'
 *   - isPortrait: boolean
 *   - isLandscape: boolean
 *   - screenWidth: number
 *   - screenHeight: number
 *   - getPageSize: () => number
 */
export const useDevice = () => {
  const context = useContext(DeviceContext);

  if (!context) {
    // Fallback for components not wrapped in provider (shouldn't happen in production)
    console.warn('useDevice must be used within a DeviceProvider');
    return {
      deviceType: DEVICE_TYPES.DESKTOP,
      isMobile: false,
      isTablet: false,
      isDesktop: true,
      isTouch: false,
      hasHover: true,
      orientation: 'landscape',
      isPortrait: false,
      isLandscape: true,
      screenWidth: 1200,
      screenHeight: 800,
      getPageSize: () => 100
    };
  }

  return context;
};

export { DEVICE_TYPES };
export default DeviceContext;
